import { ApiService } from '@/api';
import {
  GCoinsAdminActionForm,
  GCoinsHistoryFilters,
  GCoinGeeksHistory,
  GCoinsToImport,
  GeeksWithdrawal,
  WaitingWithdrawalFilters,
  WaitingWithdrawalForm,
  WaitingRequest,
  WaitingRequestForm,
  WaitingRequestFilters,
  WaitingWithdrawalAcceptForm,
  WaitingRequestRejectForm,
  ExportWithdrawalsFilters,
} from '@/models/admin-panel';
import {
  CustomAwardForm,
  GCoinGeekHistory,
  GeeksBalance,
  GeeksBalanceFilters,
  RewardableActivity,
  WithdrawForm,
} from '@/models/gcoins';

export const GCOINS_HISTORY_LIMIT = 25;

export class GCoinsApi {
  private readonly gCoinsBaseUrl = '/api/gcoins';
  private readonly geeksBaseUrl = '/api/geeks';

  public async addGCoins(formData: GCoinsAdminActionForm): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/grant`,
      formData,
    );
  }

  public async fetchAwards(): Promise<RewardableActivity[]> {
    const response = await ApiService.get(`${this.gCoinsBaseUrl}/awards`);

    return response.json();
  }

  public async fetchGCoinsHistory(
    filters?: GCoinsHistoryFilters,
    offset: number = 0,
    limit: number = GCOINS_HISTORY_LIMIT,
  ): Promise<{ data: GCoinGeeksHistory[], totalCount: number }> {
    const response = await ApiService.get(
      `${this.gCoinsBaseUrl}/transactions`,
      {
        params: {
          offset,
          limit,
          ...filters,
        },
      },
    );

    const data: GCoinGeeksHistory[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async fetchAllGeeksBalance(): Promise<string> {
    const response = await ApiService.get(`${this.gCoinsBaseUrl}/balance/sum`);

    return response.json();
  }

  public async fetchWaitingRequests(
    filters?: WaitingRequestFilters,
    offset: number = 0,
    limit: number = GCOINS_HISTORY_LIMIT,
  ): Promise<{ data: WaitingRequest[], totalCount: number }> {
    const response = await ApiService.get(
      `${this.gCoinsBaseUrl}/requests`,
      {
        params: {
          offset,
          limit,
          ...filters,
        },
      },
    );
    const data: WaitingRequest[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async fetchWithdrawalsToExport(
    filters?: ExportWithdrawalsFilters,
    offset: number = 0,
    limit: number = GCOINS_HISTORY_LIMIT,
  ): Promise<{ data: GeeksWithdrawal[], totalCount: number }> {
    const response = await ApiService.get(
      `${this.gCoinsBaseUrl}/withdrawals/accepted`,
      {
        params: {
          offset,
          limit,
          ...filters,
        },
      },
    );

    const data: GeeksWithdrawal[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async fetchWithdrawals(filters: ExportWithdrawalsFilters): Promise<Blob> {
    const response = await ApiService.get(`${this.gCoinsBaseUrl}/withdrawals/accepted/report`,
      { params: { ...filters } },
    );

    return response.blob();
  }

  public async fetchWaitingWithdrawals(
    filters?: WaitingWithdrawalFilters,
    offset: number = 0,
    limit: number = GCOINS_HISTORY_LIMIT,
  ): Promise<{ data: GeeksWithdrawal[], totalCount: number }> {
    const response = await ApiService.get(
      `${this.gCoinsBaseUrl}/withdrawals`,
      {
        params: {
          offset,
          limit,
          ...filters,
        },
      },
    );

    const data: GeeksWithdrawal[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async fetchGeeksBalanceEntries(
    filters?: GeeksBalanceFilters,
    offset: number = 0,
    limit: number = 25,
  ): Promise<{ data: any[], totalCount: number }> {

    const response = await ApiService.get(
      `${this.gCoinsBaseUrl}/balance`,
      {
        params: {
          offset,
          limit,
          ...filters,
        },
      },
    );

    const data: GeeksBalance[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async fetchBalances(): Promise<Blob> {
    const response = await ApiService.get(`${this.gCoinsBaseUrl}/balance/report`);

    return response.blob();
  }

  public async fetchGeekHistory(
    offset: number = 0,
    limit: number = GCOINS_HISTORY_LIMIT,
  ): Promise<{ data: GCoinGeekHistory[], totalCount: number }> {
    const response = await ApiService.get(
      `${this.geeksBaseUrl}/self/gcoins/history`,
      {
        params: {
          offset,
          limit,
        },
      },
    );

    const data: GCoinGeekHistory[] = await response.json();
    const totalCount: number = parseInt(response.headers.get('x-items-count') as string, 10);

    return new Promise((resolve) => resolve({ data, totalCount }));
  }

  public async withdraw(formData: WithdrawForm): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/withdrawals`,
      formData,
    );
  }

  public async exchangeGCoins(formData: GCoinsAdminActionForm): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/exchange`,
      formData,
    );
  }

  public async requestCustomAward(formData: CustomAwardForm): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/custom-awards`,
      formData,
    );
  }

  public async acceptWithdrawal(formData: WaitingWithdrawalForm): Promise<void> {
     await ApiService.post(
       `${this.gCoinsBaseUrl}/withdrawals/accept`,
       formData,
     );
  }

  public async rejectWithdrawal(formData: WaitingWithdrawalForm): Promise<void> {
     await ApiService.post(
       `${this.gCoinsBaseUrl}/withdrawals/reject`,
       formData,
     );
  }

  public async acceptRequest(formData: WaitingWithdrawalAcceptForm): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/requests/accept`,
      formData,
    );
  }

  public async rejectRequest(formData: WaitingRequestRejectForm): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/requests/reject`,
      formData,
    );
  }

  public async orderRequest(formData: WaitingRequestForm): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/requests/order`,
      formData,
    );
  }

  public async confirmRequest(formData: WaitingRequestForm): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/requests/confirm`,
      formData,
    );
  }

  public async handleGCoinsToImportData(fileData: string): Promise<GCoinsToImport[]> {
    const response = await ApiService.post(
      `${this.gCoinsBaseUrl}/import/mapped-models`,
      fileData,
    );

    return response.json();
  }

  public async sendGCoinsToImportData(gCoinsToImport: GCoinsToImport[]): Promise<void> {
    await ApiService.post(
      `${this.gCoinsBaseUrl}/import`,
      gCoinsToImport,
    );
  }
}
